/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo, useEffect } from 'react';
import {
	SCOPE_OF_ADVICE,
	NEEDS_ANALYSIS,
	CLIENT_QUOTED,
	FULL_NEEDS_ANALYSIS,
	CLIENT_TYPE,
} from '@/components/forms/CreateNewBusinessForm/validationSchema';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import RiskClassContext from '@/components/RiskClassContext';

export const RiskClasses = ({ disabled }) => {
	const { dealRow } = useObjectContext();

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		}
	);

	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;
	const { data: classesOfRisk, ...classesOfRiskQuery } = useGetClassesOfRiskQuery(
		{ countryCode, clientType: dealRow?.[CLIENT_TYPE], brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized }
	);

	const scopeOfAdvice = useMemo(
		() => dealRow?.[SCOPE_OF_ADVICE] ?? [],
		[dealRow?.[SCOPE_OF_ADVICE]]
	);

	const items = useMemo(
		() => classesOfRisk?.filter((v) => scopeOfAdvice.includes(v.classOfRisk)) ?? [],
		[scopeOfAdvice, classesOfRisk]
	);

	const [updateDeal, { isLoading }] = useUpdateDealRowMutation();

	useEffect(() => {
		// Use effect hook to work with old process of storing full needs analysis value
		if (scopeOfAdvice.includes(FULL_NEEDS_ANALYSIS) && classesOfRisk?.length) {
			onChange(classesOfRisk, true);
		}
	}, [scopeOfAdvice, classesOfRisk]);

	const onChange = async (newItems) => {
		const newValues = newItems.map((v) => (typeof v === 'string' ? v : v.classOfRisk));

		const needsAnalysis = (dealRow?.[NEEDS_ANALYSIS] ?? []).slice();
		const entries = Array.from(new Set(Object.keys(needsAnalysis).concat(newValues)));

		const vals = entries.reduce((acc, key) => {
			if (Object.keys(needsAnalysis).includes(key)) {
				const newVal = needsAnalysis.find((k) => k.key === key);
				if (newVal) {
					acc[key] = newVal;
				}
			} else {
				acc[key] = CLIENT_QUOTED;
			}
			return acc;
		}, {});

		const needsAnalysisArray = Object.entries(vals).map(([key, value]) => ({ key, value }));

		if (dealRow?.dealId) {
			await updateDeal({
				dealId: dealRow?.dealId,
				docId: dealRow?.id,
				properties: {
					[SCOPE_OF_ADVICE]: newValues,
					[NEEDS_ANALYSIS]: needsAnalysisArray,
				},
			})
				.unwrap()
				.catch((e) => console.log('Error updating deal row', e));
		}
	};

	return (
		<RiskClassContext
			{...{
				items,
				setItems: (v) => onChange(v),
				disabled,
				clientType: dealRow?.[CLIENT_TYPE],
			}}
		/>
	);
};
